<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <strong>Prompts PMAU</strong>
          </header>

          <div class="card-body p-0">
            <b-tabs card @activate-tab="aoTrocarAba">

            <!-- ── Aba prompts desta conta (técnico e admin) ─────────────────── -->
            <b-tab title="Prompts desta conta" active>
              <b-alert v-if="isAdmin" show variant="info" class="py-2 small">
                <i class="fa fa-info-circle mr-1"></i>
                Esta aba edita os prompts <strong>somente desta conta</strong>. Para mudar o
                prompt usado por todas as contas, use a aba "Prompt padrão".
              </b-alert>
              <PmauContaPrompts ref="abaConta" />
            </b-tab>

            <!-- ── Aba prompt padrão (admin) ─────────────────────────────────── -->
            <b-tab v-if="isAdmin" title="Prompt padrão (todas as contas)">
              <b-alert show variant="warning" class="py-2 small">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                Esta aba edita o prompt <strong>padrão do sistema</strong>: vale para todas as
                contas que não criaram a própria versão do prompt.
              </b-alert>

              <div class="d-flex justify-content-end mb-3">
                <b-button variant="primary" size="sm" @click="abrirCriacao">
                  <i class="fa fa-plus mr-1"></i> Adicionar figura a um capítulo
                </b-button>
              </div>

              <div v-if="loading" class="loading-container">
                <RotateSquare class="loading-position animated fadeIn" size="60px" />
              </div>

              <div v-else>
            <!-- Filtros -->
            <div class="row mb-3">
              <div class="col-lg-4 col-md-5 col-sm-12">
                <div class="form-group">
                  <label>Filtrar por grupo</label>
                  <b-form-select
                    v-model="filtroGrupo"
                    :options="opcoesGrupo"
                    @change="filtrar"
                  />
                </div>
              </div>
              <div class="col-lg-4 col-md-5 col-sm-12">
                <div class="form-group">
                  <label>Filtrar por tipo</label>
                  <b-form-select
                    v-model="filtroTipo"
                    :options="opcoesTipo"
                    @change="filtrar"
                  />
                </div>
              </div>
              <div class="col-lg-4 col-md-2 col-sm-12 d-flex align-items-end pb-3">
                <button class="btn btn-secondary" @click="limparFiltros">Limpar</button>
              </div>
            </div>

            <!-- Tabela -->
            <b-table
              :hover="true"
              responsive
              :items="itensFiltrados"
              :fields="fields"
              striped
              show-empty
              empty-text="Nenhum prompt encontrado."
            >
              <template v-slot:empty="scope">
                <h4>{{ scope.emptyText }}</h4>
              </template>

              <template v-slot:cell(stage_code)="data">
                {{ labelEtapa(data.item.stage_code) }}
              </template>

              <template v-slot:cell(prompt_type)="data">
                <b-badge :variant="tipoBadgeVariant(data.item.prompt_type)">
                  {{ data.item.prompt_type }}
                </b-badge>
              </template>

              <template v-slot:cell(is_active)="data">
                <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
                  {{ data.item.is_active ? "Ativo" : "Inativo" }}
                </b-badge>
              </template>

              <template v-slot:cell(updated_at)="data">
                {{ formatarData(data.item.updated_at) }}
              </template>

              <template v-slot:cell(acoes)="data">
                <b-button
                  variant="info"
                  size="sm"
                  title="Editar"
                  @click="abrirEdicao(data.item)"
                >
                  <i class="fa fa-edit"></i>
                </b-button>
                <b-button
                  v-if="isImagePrompt(data.item.prompt_type)"
                  variant="danger"
                  size="sm"
                  class="ml-1"
                  title="Excluir figura"
                  @click="excluir(data.item)"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </template>
            </b-table>
              </div>
            </b-tab>

            </b-tabs>
          </div><!-- card-body -->
        </div>
      </div>
    </div>

    <!-- Modal de Edição -->
    <b-modal
      v-model="modalAberto"
      size="xl"
      scrollable
      :title="itemEdicao ? itemEdicao.name : 'Editar Prompt'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="salvando"
      @ok="salvar"
      @hidden="fecharModal"
    >
      <div v-if="itemEdicao" class="pmau-form">
        <!-- Cabeçalho: metadados + status -->
        <div class="d-flex flex-wrap align-items-center mb-3">
          <b-badge :variant="tipoBadgeVariant(itemEdicao.prompt_type)" class="mr-2">
            {{ itemEdicao.prompt_type }}
          </b-badge>
          <code class="small text-muted mr-3">{{ itemEdicao.stage_code }}</code>
          <b-form-checkbox v-model="edicaoAtivo" switch class="ml-auto">
            {{ isImagePrompt(itemEdicao.prompt_type) ? "Figura ativa" : "Prompt ativo" }}
          </b-form-checkbox>
        </div>

        <b-alert
          v-if="isImagePrompt(itemEdicao.prompt_type)"
          show
          variant="light"
          class="py-2 small border"
        >
          Com a figura <strong>desativada</strong>, o capítulo é gerado sem ela. Os campos
          abaixo e o prompt são salvos juntos.
        </b-alert>
        <div v-else-if="itemEdicao.description" class="alert alert-info py-2 small">
          {{ itemEdicao.description }}
        </div>

        <!-- ── Figura: formulário estruturado ── -->
        <template v-if="isImagePrompt(itemEdicao.prompt_type)">
          <h6 class="pmau-form-section">Posicionamento</h6>
          <b-form-group
            label="Marcador"
            label-class="font-weight-bold"
            description="Cole este token no prompt user do capítulo, exatamente onde a figura deve aparecer. Vazio = figura ao final do texto."
          >
            <b-form-input v-model="edicaoFig.marker" placeholder="[IMAGEM_EXEMPLO]" />
          </b-form-group>

          <h6 class="pmau-form-section">Geração</h6>
          <div class="row">
            <b-form-group
              class="col-md-7"
              label="Tipo de geração"
              label-class="font-weight-bold"
            >
              <b-form-select v-model="edicaoFig.engine" :options="engineOptions(true)" />
            </b-form-group>
            <b-form-group
              v-if="edicaoFig.engine === 'search'"
              class="col-md-5"
              label="Número de fotos"
              label-class="font-weight-bold"
            >
              <b-form-input v-model="edicaoFig.count" type="number" min="1" max="10" />
            </b-form-group>
          </div>

          <template v-if="isGenerateEngine(edicaoFig.engine)">
            <b-form-group
              label="Legenda"
              label-class="font-weight-bold"
              description="Texto em itálico abaixo da figura no documento."
            >
              <b-form-input
                v-model="edicaoFig.caption"
                placeholder="Legenda em {conta_name}. Fonte: Arboran, {ano}."
              />
            </b-form-group>
            <b-form-group
              label="Texto alternativo (alt)"
              label-class="font-weight-bold"
              description="Descrição da imagem para acessibilidade."
            >
              <b-form-input v-model="edicaoFig.alt" />
            </b-form-group>
          </template>

          <b-form-group
            :label="bodyLabel(edicaoFig.engine)"
            label-class="font-weight-bold"
            :description="bodyHint(edicaoFig.engine)"
          >
            <b-form-textarea v-model="edicaoCorpo" rows="12" class="pmau-mono" />
          </b-form-group>
        </template>

        <!-- ── Demais prompts: conteúdo cru + variáveis ── -->
        <template v-else>
          <div class="alert alert-secondary py-2 mb-3">
            <small><strong>Variáveis disponíveis</strong>
              <span class="text-muted ml-1">(sintaxe <code>{variavel}</code>)</span></small>
            <div class="mt-2">
              <code
                v-for="v in variaveisTodas(itemEdicao)"
                :key="v"
                class="mr-2 mb-1 d-inline-block bg-light border rounded px-1 cursor-pointer"
                style="font-size: 0.8em;"
                :title="'Clique para copiar: ' + v"
                @click="copiarVariavel(v)"
              >{{ '{' + v + '}' }}</code>
            </div>
          </div>
          <b-form-group
            :label="'Conteúdo do prompt (' + edicaoConteudo.length + ' caracteres)'"
            label-class="font-weight-bold"
          >
            <b-form-textarea
              v-model="edicaoConteudo"
              rows="18"
              class="pmau-mono"
              placeholder="Conteúdo do prompt..."
            />
          </b-form-group>
        </template>
      </div>
    </b-modal>

    <!-- Modal de Criação de Figura -->
    <b-modal
      v-model="modalCriacao"
      size="xl"
      scrollable
      title="Adicionar figura a um capítulo"
      ok-title="Criar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="criando"
      @ok="criar"
      @hidden="fecharCriacao"
    >
      <div class="pmau-form">
        <b-alert show variant="light" class="py-2 small border">
          A figura só é gerada quando o <strong>marcador</strong> é citado no prompt
          <code>user</code> do capítulo. Os campos e o prompt são salvos juntos.
        </b-alert>

        <h6 class="pmau-form-section">Capítulo</h6>
        <div class="row">
          <b-form-group class="col-md-8" label="Capítulo / sub-etapa" label-class="font-weight-bold">
            <b-form-select v-model="novoStageCode" :options="opcoesEtapaCriacao" />
          </b-form-group>
          <b-form-group
            class="col-md-4"
            label="Slot"
            label-class="font-weight-bold"
            description="image_prompt_1 / _2 … para mais de uma figura no mesmo capítulo."
          >
            <b-form-select v-model="novoPromptType" :options="opcoesTipoFigura" />
          </b-form-group>
        </div>
        <div class="row align-items-center">
          <b-form-group class="col-md-8 mb-2" label="Nome" label-class="font-weight-bold">
            <b-form-input v-model="novoNome" placeholder="Ex.: 2.3 Solo — Figura 1" />
          </b-form-group>
          <div class="col-md-4">
            <b-form-checkbox v-model="novoAtivo" switch>Figura ativa</b-form-checkbox>
          </div>
        </div>

        <h6 class="pmau-form-section">Posicionamento</h6>
        <b-form-group
          label="Marcador"
          label-class="font-weight-bold"
          description="Cole este token no prompt user do capítulo, onde a figura deve aparecer."
        >
          <b-form-input v-model="novoFig.marker" placeholder="[IMAGEM_EXEMPLO]" />
        </b-form-group>

        <h6 class="pmau-form-section">Geração</h6>
        <div class="row">
          <b-form-group class="col-md-7" label="Tipo de geração" label-class="font-weight-bold">
            <b-form-select v-model="novoFig.engine" :options="engineOptions()" />
          </b-form-group>
          <b-form-group
            v-if="novoFig.engine === 'search'"
            class="col-md-5"
            label="Número de fotos"
            label-class="font-weight-bold"
          >
            <b-form-input v-model="novoFig.count" type="number" min="1" max="10" />
          </b-form-group>
        </div>

        <template v-if="isGenerateEngine(novoFig.engine)">
          <b-form-group
            label="Legenda"
            label-class="font-weight-bold"
            description="Texto em itálico abaixo da figura no documento."
          >
            <b-form-input
              v-model="novoFig.caption"
              placeholder="Legenda em {conta_name}. Fonte: Arboran, {ano}."
            />
          </b-form-group>
          <b-form-group
            label="Texto alternativo (alt)"
            label-class="font-weight-bold"
            description="Descrição da imagem para acessibilidade."
          >
            <b-form-input v-model="novoFig.alt" />
          </b-form-group>
        </template>

        <b-form-group
          :label="bodyLabel(novoFig.engine)"
          label-class="font-weight-bold"
          :description="bodyHint(novoFig.engine)"
        >
          <b-form-textarea
            v-model="novoCorpo"
            rows="10"
            class="pmau-mono"
            :placeholder="novoFig.engine === 'generate'
              ? 'Descreva a ilustração a ser gerada...'
              : novoFig.engine === 'diagram'
                ? 'Descreva as etapas, os itens e os textos do fluxograma...'
                : 'Ex.: {cidade} praça arborizada'"
          />
        </b-form-group>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauContaPrompts from "./PmauContaPrompts";
import PmauService from "../../servico/PmauService";
import { STAGE_LABELS, compararEtapa } from "../../enums/PmauStageLabels";

const TYPE_VARIANT = {
  system: "dark",
  user: "primary",
  queries: "warning",
  insert: "info",
  fixed_text: "secondary",
  options: "success",
  html_directives: "light",
  html_directives_location: "light",
  queries_system: "warning",
  image_prompt: "danger"
};

// ── Cabeçalho de figura (@marker / @engine / …) — editado como campos, salvo no content ──
const FIGURE_HEADER_KEYS = ["marker", "engine", "caption", "alt", "count", "filename"];

const ENGINE_OPTIONS = [
  { value: "generate", text: "Gerar imagem com IA (ilustração)" },
  { value: "diagram", text: "Fluxograma/diagrama com texto (IA escreve, Graphviz desenha)" },
  { value: "search", text: "Pesquisar fotos reais (Serper)" },
  { value: "search_distributed", text: "Pesquisar fotos — distribuídas no texto pela IA" },
  { value: "search_species", text: "Pesquisar fotos por espécie (a IA monta a figura)" }
];

function emptyFig() {
  return { marker: "", engine: "generate", caption: "", alt: "", count: "3", filename: "" };
}

// content -> { fig, extra, body }. Campos ausentes vêm em branco (preenchidos por
// mergeFigDefaults com os defaults do backend). Sem cabeçalho -> body = content inteiro.
function parseFigureContent(content) {
  content = content || "";
  const fig = { marker: "", engine: "", caption: "", alt: "", count: "", filename: "" };
  const extra = {};
  if (!/^\s*@\w+\s*:/.test(content)) return { fig, extra, body: content };
  const lines = content.split(/\r?\n/);
  let sep = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^---[ \t]*$/.test(lines[i])) { sep = i; break; }
  }
  if (sep === -1) return { fig, extra, body: content };
  lines.slice(0, sep).forEach((line) => {
    const m = line.match(/^\s*@(\w+)\s*:\s*(.*)$/);
    if (!m) return;
    const k = m[1].toLowerCase();
    const v = m[2].trim();
    if (FIGURE_HEADER_KEYS.indexOf(k) !== -1) fig[k] = v;
    else extra[k] = v;
  });
  return { fig, extra, body: lines.slice(sep + 1).join("\n").replace(/^\n+/, "") };
}

// Combina os campos parseados do content com os defaults do backend (figure_defaults).
function mergeFigDefaults(parsed, defaults) {
  const d = defaults || {};
  return {
    marker: parsed.marker || d.marker || "",
    engine: parsed.engine || d.engine || "generate",
    caption: parsed.caption || d.caption || "",
    alt: parsed.alt || d.alt || "",
    count: parsed.count || d.count || "3",
    filename: parsed.filename || d.filename || ""
  };
}

// { fig, extra, body } -> content (cabeçalho + --- + corpo).
function buildFigureContent(fig, extra, body) {
  const engine = fig.engine || "generate";
  const head = ["@engine: " + engine];
  if ((fig.marker || "").trim()) head.push("@marker: " + fig.marker.trim());
  // Legenda e alt só valem para figuras geradas (ilustração/diagrama), não para fotos do Serper.
  if (engine === "generate" || engine === "diagram") {
    if ((fig.caption || "").trim()) head.push("@caption: " + fig.caption.trim());
    if ((fig.alt || "").trim()) head.push("@alt: " + fig.alt.trim());
  }
  if (engine === "search" && String(fig.count || "").trim()) {
    head.push("@count: " + String(fig.count).trim());
  }
  if ((fig.filename || "").trim()) head.push("@filename: " + fig.filename.trim());
  Object.keys(extra || {}).forEach((k) => {
    if (extra[k]) head.push("@" + k + ": " + extra[k]);
  });
  return head.join("\n") + "\n---\n" + (body || "");
}

export default {
  name: "PmauPrompts",
  components: { RotateSquare, PmauContaPrompts },
  data() {
    return {
      loading: false,
      salvando: false,
      itens: [],
      filtroGrupo: null,
      filtroTipo: null,
      modalAberto: false,
      itemEdicao: null,
      edicaoConteudo: "",
      edicaoAtivo: true,
      edicaoFig: emptyFig(),
      edicaoFigExtra: {},
      edicaoCorpo: "",
      modalCriacao: false,
      criando: false,
      novoStageCode: null,
      novoPromptType: "image_prompt",
      novoNome: "",
      novoAtivo: true,
      novoFig: emptyFig(),
      novoCorpo: "",
      variavelsConta: ["conta_name"],
      variaveisPmau: [],
      fields: [
        { key: "stage_code", label: "Etapa", sortable: true },
        { key: "prompt_type", label: "Tipo", sortable: true },
        { key: "name", label: "Nome", sortable: true },
        { key: "is_active", label: "Status", sortable: true },
        { key: "updated_at", label: "Atualizado em", sortable: true },
        { key: "acoes", label: "Ações", sortable: false, thClass: "wd-80-px" }
      ]
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isPmauAdmin;
    },
    opcoesGrupo() {
      const grupos = [...new Set(this.itens.map((i) => i.stage_code))];
      grupos.sort(compararEtapa);
      return [
        { value: null, text: "Todos os grupos" },
        ...grupos.map((g) => ({ value: g, text: STAGE_LABELS[g] || g }))
      ];
    },
    opcoesTipo() {
      const tipos = [...new Set(this.itens.map((i) => i.prompt_type))];
      tipos.sort((a, b) => a.localeCompare(b, "pt-BR"));
      return [
        { value: null, text: "Todos os tipos" },
        ...tipos.map((t) => ({ value: t, text: t }))
      ];
    },
    itensFiltrados() {
      return this.itens
        .filter((item) => {
          if (this.filtroGrupo && item.stage_code !== this.filtroGrupo) return false;
          if (this.filtroTipo && item.prompt_type !== this.filtroTipo) return false;
          return true;
        })
        .sort((a, b) => {
          const byStage = compararEtapa(a.stage_code, b.stage_code);
          if (byStage !== 0) return byStage;
          return a.prompt_type.localeCompare(b.prompt_type, "pt-BR");
        });
    },
    opcoesEtapaCriacao() {
      return [
        { value: null, text: "Selecione o capítulo...", disabled: true },
        ...Object.keys(STAGE_LABELS)
          .filter((c) => c !== "__global__")
          .sort(compararEtapa)
          .map((c) => ({ value: c, text: STAGE_LABELS[c] }))
      ];
    },
    opcoesTipoFigura() {
      return ["image_prompt", "image_prompt_1", "image_prompt_2", "image_prompt_3"].map(
        (t) => ({ value: t, text: t })
      );
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.abaConta && this.$refs.abaConta.carregar());
    if (this.isAdmin) {
      this.carregarPrompts();
      this.carregarVariaveis();
    }
  },
  methods: {
    // Índice 1 = aba do prompt padrão (só existe para admin).
    aoTrocarAba(novoIndice) {
      if (novoIndice === 0) this.$refs.abaConta && this.$refs.abaConta.carregar();
    },
    carregarPrompts() {
      this.loading = true;
      PmauService.ListPrompts()
        .then((response) => {
          this.itens = response.data;
        })
        .catch((erro) => {
          this.$notify({
            data: ["Erro ao carregar prompts."],
            type: "warn",
            duration: 5000
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    carregarVariaveis() {
      PmauService.ListVariables()
        .then((r) => {
          this.variaveisPmau = r.data.map((v) => v.code);
        })
        .catch(() => {});
    },
    filtrar() {
      // computed já filtra reativamente
    },
    limparFiltros() {
      this.filtroGrupo = null;
      this.filtroTipo = null;
    },
    labelEtapa(code) {
      return STAGE_LABELS[code] || code;
    },
    tipoBadgeVariant(tipo) {
      if (tipo && tipo.indexOf("image_prompt") === 0) return "danger";
      return TYPE_VARIANT[tipo] || "secondary";
    },
    isImagePrompt(tipo) {
      return !!tipo && tipo.indexOf("image_prompt") === 0;
    },
    formatarData(isoString) {
      if (!isoString) return "";
      return new Date(isoString).toLocaleString("pt-BR");
    },
    abrirEdicao(item) {
      this.itemEdicao = item;
      this.edicaoAtivo = item.is_active;
      if (this.isImagePrompt(item.prompt_type)) {
        const p = parseFigureContent(item.content);
        this.edicaoFig = mergeFigDefaults(p.fig, item.figure_defaults);
        this.edicaoFigExtra = p.extra;
        this.edicaoCorpo = p.body;
        this.edicaoConteudo = "";
      } else {
        this.edicaoConteudo = item.content;
      }
      this.modalAberto = true;
    },
    fecharModal() {
      this.itemEdicao = null;
      this.edicaoConteudo = "";
      this.edicaoAtivo = true;
      this.edicaoFig = emptyFig();
      this.edicaoFigExtra = {};
      this.edicaoCorpo = "";
    },
    abrirCriacao() {
      this.novoStageCode = null;
      this.novoPromptType = "image_prompt";
      this.novoNome = "";
      this.novoAtivo = true;
      this.novoFig = emptyFig();
      this.novoCorpo = "";
      this.modalCriacao = true;
    },
    fecharCriacao() {
      this.novoStageCode = null;
      this.novoFig = emptyFig();
      this.novoCorpo = "";
    },
    engineOptions(all) {
      // Na criação só generate/diagram/search; search_distributed/search_species são exclusivos
      // dos 6 capítulos com pipeline próprio (aparecem só ao editar as linhas existentes).
      return all ? ENGINE_OPTIONS : ENGINE_OPTIONS.slice(0, 3);
    },
    bodyLabel(engine) {
      if (engine === "diagram") return "Descrição do diagrama";
      return engine === "generate" ? "Prompt da imagem" : "Termo de busca das fotos";
    },
    bodyHint(engine) {
      if (engine === "generate")
        return "Descrição enviada ao gerador de imagem. Aceita {conta_name}, {cidade}, {ano}.";
      if (engine === "diagram")
        return "Etapas, itens e textos exatos do fluxograma — a IA monta o diagrama e o texto sai legível. Aceita {conta_name}, {cidade}, {ano}.";
      if (engine === "search")
        return "Consulta usada no Google Imagens (Serper). Aceita {conta_name}, {cidade}.";
      return "Pipeline próprio do capítulo — o texto abaixo é apenas documentação.";
    },
    isGenerateEngine(engine) {
      return engine === "generate" || engine === "diagram";
    },
    criar(evento) {
      evento.preventDefault();
      if (!this.novoStageCode) {
        this.$notify({ data: ["Selecione o capítulo."], type: "warn", duration: 3000 });
        return;
      }
      if (!this.novoNome.trim() || !this.novoCorpo.trim()) {
        this.$notify({ data: ["Preencha o nome e o prompt/termo de busca."], type: "warn", duration: 3000 });
        return;
      }
      const jaExiste = this.itens.some(
        (i) => i.stage_code === this.novoStageCode && i.prompt_type === this.novoPromptType
      );
      if (jaExiste) {
        this.$notify({
          data: ["Já existe um prompt " + this.novoPromptType + " para este capítulo. Use image_prompt_1, _2 …"],
          type: "warn",
          duration: 5000
        });
        return;
      }
      this.criando = true;
      PmauService.CreatePrompt({
        stage_code: this.novoStageCode,
        prompt_type: this.novoPromptType,
        name: this.novoNome,
        content: buildFigureContent(this.novoFig, {}, this.novoCorpo),
        is_active: this.novoAtivo
      })
        .then((response) => {
          this.itens.push(response.data);
          this.modalCriacao = false;
          this.$notify({ data: ["Figura criada."], type: "success", duration: 4000 });
        })
        .catch((erro) => {
          const msg =
            erro && erro.response && erro.response.data && erro.response.data.detail
              ? erro.response.data.detail
              : "Erro ao criar figura.";
          this.$notify({ data: [msg], type: "warn", duration: 6000 });
        })
        .finally(() => {
          this.criando = false;
        });
    },
    excluir(item) {
      if (!window.confirm('Excluir a figura "' + item.name + '"? O capítulo passará a ser gerado sem ela.')) {
        return;
      }
      PmauService.DeletePrompt(item.stage_code, item.prompt_type)
        .then(() => {
          this.itens = this.itens.filter(
            (i) => !(i.stage_code === item.stage_code && i.prompt_type === item.prompt_type)
          );
          this.$notify({ data: ["Figura removida."], type: "success", duration: 4000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao remover figura."], type: "warn", duration: 5000 });
        });
    },
    variaveisExtras(item) {
      if (!item) return [];
      if (this.isImagePrompt(item.prompt_type)) return ["conta_name", "cidade", "ano"];
      if (item.prompt_type === "user") {
        if (item.stage_code === "introduction_objectives")
          return ["objectives_fixed_text", "objectives_list", "html_directives"];
        if (item.stage_code === "participatory_diagnosis_results")
          return ["cidade", "survey_data", "chart_markers", "html_directives"];
        if (item.stage_code === "participatory_diagnosis_methodology")
          return ["cidade", "survey_facts", "figure_note", "html_directives"];
        return ["html_directives"];
      }
      if (item.prompt_type === "queries") {
        if (item.stage_code === "urban_planning_indicated_species"
          || item.stage_code === "urban_planning_toxic_plants")
          return ["text_content"];
        return ["cidade", "text_content"];
      }
      if (item.prompt_type === "insert") {
        if (item.stage_code === "characterization_vegetation")
          return ["images_group1_json", "images_group2_json", "text_content"];
        if (item.stage_code === "urban_planning_indicated_species"
          || item.stage_code === "urban_planning_toxic_plants")
          return ["title", "source", "images_json"];
        return ["images_json", "text_content"];
      }
      return [];
    },
    variaveisTodas(item) {
      const base = this.variavelsConta.concat(this.variaveisPmau);
      const extras = this.variaveisExtras(item).filter((v) => base.indexOf(v) === -1);
      return base.concat(extras);
    },
    copiarVariavel(variavel) {
      const texto = "{" + variavel + "}";
      navigator.clipboard.writeText(texto).then(() => {
        this.$notify({
          data: [`Copiado: ${texto}`],
          type: "success",
          duration: 2000
        });
      });
    },
    salvar(evento) {
      evento.preventDefault();

      const ehFigura = this.isImagePrompt(this.itemEdicao.prompt_type);
      const conteudo = ehFigura
        ? buildFigureContent(this.edicaoFig, this.edicaoFigExtra, this.edicaoCorpo)
        : this.edicaoConteudo;

      if (!(ehFigura ? this.edicaoCorpo : this.edicaoConteudo).trim()) {
        this.$notify({
          data: [ehFigura ? "Informe o prompt da imagem / termo de busca." : "O conteúdo do prompt não pode estar vazio."],
          type: "warn",
          duration: 3000
        });
        return;
      }

      this.salvando = true;
      PmauService.UpdatePrompt(
        this.itemEdicao.stage_code,
        this.itemEdicao.prompt_type,
        {
          content: conteudo,
          is_active: this.edicaoAtivo
        }
      )
        .then((response) => {
          const idx = this.itens.findIndex(
            (i) =>
              i.stage_code === this.itemEdicao.stage_code &&
              i.prompt_type === this.itemEdicao.prompt_type
          );
          if (idx !== -1) {
            this.$set(this.itens, idx, response.data);
          }
          this.modalAberto = false;
          this.$notify({
            data: ["Prompt atualizado com sucesso."],
            type: "success",
            duration: 4000
          });
        })
        .catch(() => {
          this.$notify({
            data: ["Erro ao salvar prompt."],
            type: "warn",
            duration: 5000
          });
        })
        .finally(() => {
          this.salvando = false;
        });
    }
  }
};
</script>

<style scoped>
.pmau-form-section {
  margin: 1.5rem 0 0.75rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #e4e7ea;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a93a2;
}
.pmau-form-section:first-child {
  margin-top: 0;
}
.pmau-form .form-group {
  margin-bottom: 1rem;
}
.pmau-form >>> .pmau-mono {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.82rem;
  line-height: 1.45;
  resize: vertical;
}
.pmau-form >>> .form-text {
  font-size: 0.78rem;
}
</style>
